import {
  OriginalReportRowsExampleJSON,
  AfterGroupByTransformationExampleJSON
} from "./constants/transformationInputOutput";
import { Button } from "@/components/ui/button";
import { ButtonVariant } from "@/constants/button/button";
import { forwardRef, useImperativeHandle, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { AppField } from "@/types/WorkflowCreate/fields";
import { getAppFieldsAccordingToType } from "@/helpers/appFields";
import { useSelector } from "react-redux";
import { RootState } from "@/redux";
import { AppFieldsType, CampArrayFieldOptions } from "@/constants/workflows/workflowCreate";
import { NestedDropdown } from "@/components/custom/MultiLayeredDropdownField";
import { cn } from "@/lib/utils";
import { InputFieldLabel } from "@/components/custom/InputField/Label";
import { DestinationSelectInputField } from "@/components/custom/InputField/WorkflowSelectInput";
import { GroupByProps, GroupByRef, ValidationErrors } from "./types/groupByAndAggregate";

export const GroupByAndAggregate = forwardRef<GroupByRef, GroupByProps>(({
  allFields,
  initialConfiguration,
}, ref) => {
  const { applications } = useSelector((state: RootState) => state.application);
  const [groupByField, setGroupByField] = useState<string>(initialConfiguration?.groupByField || '');
  const [aggregation, setAggregation] = useState<Array<{
    aggregationFieldName: string;
    fields: Array<{ field: string; fieldLabel?: string; }>;
  }>>(() => {
    if (initialConfiguration?.aggregateArrays && initialConfiguration.aggregateArrays.length > 0) {
      return initialConfiguration.aggregateArrays.map((agg) => ({
        aggregationFieldName: agg.name || '',
        fields: agg.fields?.map((f: string) => ({ field: f, fieldLabel: '' })) || []
      }));
    }
    return [
      {
        aggregationFieldName: '',
        fields: []
      }
    ];
  });
  const [errors, setErrors] = useState<ValidationErrors>({
    groupByField: false,
    aggregations: [{ hasError: false, fieldErrors: [] }]
  });

  useImperativeHandle(ref, () => ({
    validate: () => {
      const newErrors: ValidationErrors = {
        groupByField: !groupByField.trim(),
        aggregations: []
      };

      // Validate each aggregation
      aggregation.forEach((agg) => {
        const aggError = {
          hasError: false,
          fieldErrors: [] as boolean[]
        };

        // If aggregationFieldName is defined, check that at least one field exists and is not empty
        if (agg.aggregationFieldName.trim()) {
          const validFields = agg.fields.filter(f => f.field.trim());

          if (validFields.length === 0) {
            aggError.hasError = true;
          }

          // Mark each field as error if empty
          aggError.fieldErrors = agg.fields.map(f => !f.field.trim());
        }

        newErrors.aggregations.push(aggError);
      });

      setErrors(newErrors);

      // Return true if no errors
      const hasGroupByError = newErrors.groupByField;
      const hasAggregationErrors = newErrors.aggregations.some(agg => agg.hasError || agg.fieldErrors.some(e => e));

      return !hasGroupByError && !hasAggregationErrors;
    },
    getData: () => {
      // Find the field label for groupByField
      const allResponseFields = getAppFieldsAccordingToType(
        allFields,
        AppFieldsType.RESPONSE_TYPE,
        applications
      );

      let groupByFieldCodeChain = groupByField;
      // Search through all fields to find the display name
      const findFieldCodeChain = (fields: AppField[], codeChain: string): string | null => {
        for (const field of fields) {
          if (field.codeChain === codeChain) {
            return field.code || field.displayName; // fallback to displayName if code is not available
          }
          if (field.fields && field.fields.length > 0) {
            const found = findFieldCodeChain(field.fields, codeChain);
            if (found) return found;
          }
        }
        return null;
      };

      const foundCodeChain = findFieldCodeChain(allResponseFields, groupByField);
      if (foundCodeChain) {
        groupByFieldCodeChain = foundCodeChain;
      }

      return {
        groupByField: groupByFieldCodeChain,
        aggregateArrays: aggregation
          .filter(agg => agg.aggregationFieldName.trim())
          .map(agg => ({
            name: agg.aggregationFieldName,
            fields: agg.fields
              .filter(f => f.field.trim())
              .map(f => f.field)
          }))
      };
    }
  }))

  return (
    <div className="text-primary flex flex-col max-h-[500px] min-h-[300px] h-full">
      <div className="flex-1 overflow-y-auto thin-scrollbar pr-2">
        <div className="flex w-full mb-4">
          <div className="w-1/2 flex-col items-start">
            <p className="text-sm font-medium">
              Example original rows
            </p>
            <div className="bg-foreground rounded-md border p-4 mt-1 max-h-[250px] overflow-auto thin-scrollbar">
              <pre className="text-xs">
                {JSON.stringify(OriginalReportRowsExampleJSON, null, 2)}
              </pre>
            </div>
          </div>
          <div className="mx-1" />
          <div className="w-1/2">
            <p className="text-sm font-medium">Transformed rows after Grouping By Family Id</p>
            <div className="bg-foreground rounded-md border p-4 mt-1 max-h-[250px] overflow-auto thin-scrollbar">
              <pre className="text-xs">
                {JSON.stringify(AfterGroupByTransformationExampleJSON, null, 2)}
              </pre>
            </div>
          </div>
        </div>
        <div className="mb-6">
          <NestedDropdown
            data={getAppFieldsAccordingToType(
              allFields,
              AppFieldsType.RESPONSE_TYPE,
              applications
            )}
            field={groupByField}
            label="Group By"
            required={true}
            description="Select a field to group by. e.g. 'Family Id' groups all members of the same family in one group."
            initialSelectedLabel={groupByField}
            onSelect={(item) => {
              setGroupByField(item.codeChain);
              setErrors(prev => ({ ...prev, groupByField: false }));
            }}
            showErrorFields={errors.groupByField}
          />
          {errors.groupByField && (
            <p className="text-destructive text-xs mt-1">Group By field is required</p>
          )}
        </div>
        <InputFieldLabel label="Aggregations" />
        {aggregation.map((agg, index) => (
          <div key={`aggregation-${index}`} className="mb-4 rounded-md border border-accent bg-accent-background p-4">
            <div className="flex items-start gap-4">
              <div className="">
                {/* TODO This should be InputField in the future, we are forcing to aggregate just camper's fields or parents' fields */}
                <InputFieldLabel
                  label="Select Aggregation Name"
                  description="Give a name to the aggregated field array, e.g., 'Campers' or 'Parents'"
                />
                <DestinationSelectInputField
                  value={agg.aggregationFieldName}
                  selectOptions={CampArrayFieldOptions}
                  onChange={(value) => {
                    setAggregation((prev) =>
                      prev.map((item, idx) =>
                        idx === index ? { ...item, aggregationFieldName: value } : item
                      )
                    )
                  }}
                  additionalClassName={cn('bg-foreground', 'max-w-[280px]')}
                />
                {/* TODO remove select field from above and uncomment this code */}
                {/* <InputFieldLabel
                  label="Type key name for Aggregated Fields"
                  description="E.g. 'Campers' if you are aggregating camper fields"
                />
                <Input
                  className={cn(INPUT_FIELD_CLASSNAME, 'bg-foreground', 'max-w-[280px]')}
                  type="text"
                  value={agg.aggregationFieldName}
                  placeholder="Type field aggregation name"
                  onChange={(event) => {
                    const { value } = event.target
                    setAggregation((prev) =>
                      prev.map((item, idx) =>
                        idx === index ? { ...item, aggregationFieldName: value } : item
                      )
                    )
                  }}
                /> */}
              </div>
              {(aggregation.length ?? 0) > 1 ? (
                <Button
                  variant={ButtonVariant.ICON}
                  className="ml-auto"
                  onClick={() => {
                    setAggregation((prev) => prev.filter((_, idx) => idx !== index))
                  }}
                >
                  <Trash2 className="text-destructive" size={16} />
                </Button>
              ) : null}
            </div>
            <div className="mt-4 space-y-3">
              {agg.aggregationFieldName ? (
                <p className="text-secondary text-sm font-normal -mt-1 mb-1">
                  Add fields that roll up into “{agg.aggregationFieldName.trim().length ? agg.aggregationFieldName : 'this group'}”.
                </p>
              ) : (
                <p className="text-secondary text-sm font-normal -mt-1 mb-1">
                  Add fields to include in this aggregated group.
                </p>
              )}
              {errors.aggregations[index]?.hasError && (
                <p className="text-destructive text-xs">At least one field is required when aggregation name is specified</p>
              )}
              {agg.fields.map((field, fieldIndex) => (
                <div key={`aggregation-${index}-field-${fieldIndex}`} className="flex items-center gap-4">
                  <div className="w-2/3 max-w-[280px]">
                    <NestedDropdown
                      data={getAppFieldsAccordingToType(
                        allFields,
                        AppFieldsType.RESPONSE_TYPE,
                        applications
                      )}
                      field={field.field}
                      label="Field"
                      initialSelectedLabel={field.fieldLabel || field.field}
                      onSelect={(item) => {
                        setAggregation((prev) =>
                          prev.map((aggregationItem, aggregationIndex) => {
                            if (aggregationIndex !== index) {
                              return aggregationItem
                            }

                            return {
                              ...aggregationItem,
                              fields: aggregationItem.fields.map((fieldItem, fieldIdx) =>
                                fieldIdx === fieldIndex
                                  ? { field: item.codeChain, fieldLabel: item.displayName }
                                  : fieldItem
                              )
                            }
                          })
                        );
                        // Clear field error when selecting
                        setErrors(prev => ({
                          ...prev,
                          aggregations: prev.aggregations.map((aggErr, aggIdx) =>
                            aggIdx === index
                              ? {
                                ...aggErr,
                                hasError: false,
                                fieldErrors: aggErr.fieldErrors.map((err, fIdx) => fIdx === fieldIndex ? false : err)
                              }
                              : aggErr
                          )
                        }));
                      }}
                      showErrorFields={errors.aggregations[index]?.fieldErrors[fieldIndex]}
                    />
                  </div>
                  <Button
                    variant={ButtonVariant.ICON}
                    onClick={() =>
                      setAggregation((prev) =>
                        prev.map((aggregationItem, aggregationIdx) => {
                          if (aggregationIdx !== index) {
                            return aggregationItem
                          }

                          return {
                            ...aggregationItem,
                            fields: aggregationItem.fields.filter((_, idx) => idx !== fieldIndex)
                          }
                        })
                      )
                    }
                  >
                    <Trash2 className="text-destructive mt-4" size={16} />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                className="font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent"
                variant={ButtonVariant.OUTLINE}
                onClick={() =>
                  setAggregation((prev) =>
                    prev.map((aggregationItem, aggregationIndex) =>
                      aggregationIndex === index
                        ? {
                          ...aggregationItem,
                          fields: [...aggregationItem.fields, { field: '', fieldLabel: '' }]
                        }
                        : aggregationItem
                    )
                  )
                }
              >
                <Plus className="mr-2 w-4 h-4" />
                Add Fields
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-auto">
        <Button
          type="button"
          className="mt-4 font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent"
          variant={ButtonVariant.OUTLINE}
          onClick={() =>
            setAggregation((prev) => [
              ...prev,
              {
                aggregationFieldName: '',
                fields: []
              }
            ])
          }
        >
          <Plus className="mr-2 w-4 h-4" />
          Add another Aggregation
        </Button>
      </div>
    </div>
  );
});
